import { IAppleSignInSetting, ICommonSettings, IImageLibraryDto } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { ReactNode } from "react";
import { ImageLibraryService } from "../../../../../services/image-library-service";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigFilePicker } from "../config-file-picker";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigAppleSignInState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingAppleSignIn?: IAppleSignInSetting;
  allImage?: IImageLibraryDto[];
  imageSearchText: string;
  fileName: string;
  showFileIsRequiredError?: boolean;
}

export interface IConfigAppleSignInProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingAppleSignInId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigAppleSignIn extends React.PureComponent<IConfigAppleSignInProps, IConfigAppleSignInState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigAppleSignInProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingAppleSignIn: undefined,
      allImage: [],
      imageSearchText: "",
      fileName: ""
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigAppleSignInProps & IWithSDProps) {
    if (prevProps.editingAppleSignInId !== this.props.editingAppleSignInId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    this.setState({ showFileIsRequiredError: false });
    if (this.props.editingAppleSignInId) {
      const appleSignInSetting = this.props.commonSettings.appleSignInSettings?.find(
        (x) => x.id === this.props.editingAppleSignInId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingAppleSignIn: appleSignInSetting }, () => {
        if (appleSignInSetting) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.appleSignInName = appleSignInSetting.name;
          fieldsValue.teamId = appleSignInSetting.teamId;
          fieldsValue.p8FileName = appleSignInSetting.p8FileName;

          this.formRef.current?.setFieldsValue(fieldsValue);
          this.setState({ fileName: appleSignInSetting.p8FileName });
        } else {
          this.setState({ fileName: "" });
        }
      });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const setting = this.props.commonSettings.appleSignInSettings;

    const isSettingAlreadyExist =
      setting?.some(
        (x) => x.name === value && x.id !== this.props.editingAppleSignInId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("Apple Sign In Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    if (!this.state.fileName) {
      this.setState({ showFileIsRequiredError: true });
      return;
    }
    this.saveStatus.start();
    let obj: IAppleSignInSetting;
    if (this.props.editingAppleSignInId) {
      obj = {
        id: this.props.editingAppleSignInId,
        name: data.appleSignInName,
        teamId: data.teamId?.trim(),
        p8FileName: this.state.fileName
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "appleSignInSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.appleSignInName,
        teamId: data.teamId?.trim(),
        p8FileName: this.state.fileName
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "appleSignInSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteAppleSignIn = () => {
    if (!this.props.editingAppleSignInId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingAppleSignInId || "",
      configKey: "appleSignInSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Apple Sign In setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  getImageLibrary = async () => {
    try {
      const res = await ImageLibraryService.GetAll();
      if (res && res.status === 200) {
        this.setState({ allImage: res.data });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
    }
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Apple sign in setting"
        secondHeader="Apple sign in setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingAppleSignInId ? this.deleteAppleSignIn : undefined}
            deleteTestId="appleSignInSettingDeleteButton"
            submitTestId={`appleSignInSetting${this.props.editingAppleSignInId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingAppleSignInId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAppleSignInForm"
          />
        }
      >
        <Form
          id="configAppleSignInForm"
          name="appleSignInSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("applesigninsettingspop_up_applesigninsetting_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="applesigninsettingspop_up_applesigninsetting_name">
              <Form.Item
                name="appleSignInName"
                rules={[
                  {
                    required: true,
                    message: "Please enter a Apple Sign In Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Apple Sign In Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="appleSignInSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Team Id"
              required
              tooltipContentKey="applesigninsettingspop_up_applesigninsetting_teamid"
            >
              <Form.Item name="teamId" rules={[{ required: true, message: "Please enter a Team Id." }]}>
                <KuikaInput size="md" data-testid="appleSignInSettingTeamIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Sign In Certificate"
              required
              tooltipContentKey="applesigninsettingspop_up_applesigninsetting_signincertificate"
              error={this.state.showFileIsRequiredError ? "Please select a Apple Sign In Certificate file." : undefined}
            >
              <ConfigFilePicker
                files={this.state.allImage ?? []}
                acceptedFileTypes=".p8"
                getImageLibrary={this.getImageLibrary}
                selectedId={this.state.fileName}
                fileName={this.state.fileName}
                onSelect={(image: IImageLibraryDto): void =>
                  this.setState({ fileName: image.fileName, showFileIsRequiredError: !image.fileName })
                }
                onClear={(): void => this.setState({ fileName: "", showFileIsRequiredError: true })}
                chooseTestId="appleSignInSettingCertificateChooseButton"
              />
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
